import React, { useCallback, useState } from 'react';
import { Col, Container, Row } from 'reactstrap';
import {
  PeriodMetrics,
  ProperAssignedVisit,
  ProperPreSaleMetrics,
  ProperPreSaleVisit,
  ProperV2Metrics,
} from '../models/dashboardModels';
import useAdminEntity from '../../../hooks/useAdminEntity';
import {
  getDashboardMetricsPreSaleEndpoint,
  getDashboardMetricsV2Endpoint,
  getDashboardPreSaleVisitsEndpoint,
  getDashboardVisitsEndpoint,
} from '../services/dashboardService';
import useAuth from '../../../hooks/useAuth';
import useAdminPagination from '../../../hooks/useAdminPagination';
import AdminPaginator from '../../../components/admin/AdminPaginator';
import DashboardVisitListTable from './DashboardVisitsListTable';
import DashboardPreSaleVisitListTable from './DashboardPreSaleVisitsListTable';
import {
  toStatsMetrics,
  VISIT_STATUS_MAP,
  PRE_SALE_STATUS_MAP,
} from '../adapters/dashboardAdapters';
import MetricsDashboard from './MetricsDashboard';
import NavigationTabDashboard from './NavigationTabDashboard';
import { activeTab } from '../types/dashboard.types';
import MetricsDateDashboard from './MetricsDateDashboard';

const ProperDashboard: React.FC = () => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [activeTab, setActiveTab] = useState<activeTab>('rent');

  const defaultPeriod: PeriodMetrics = {
    end: '',
    start: '',
  };

  const getDashboardMetrics = useCallback(
    () => getDashboardMetricsV2Endpoint(token),
    [token],
  );
  const [dashboardMetrics] = useAdminEntity<ProperV2Metrics>(
    getDashboardMetrics,
    console.error,
  );

  const getDashboardPreSaleMetrics = useCallback(
    () => getDashboardMetricsPreSaleEndpoint(token),
    [token],
  );

  const [dashboardPreSaleMetrics] = useAdminEntity<ProperPreSaleMetrics>(
    getDashboardPreSaleMetrics,
    console.error,
  );

  const [dashboardVisits, setDashboardVisits] = useState<ProperAssignedVisit[]>(
    [],
  );
  const { paginator, loadItems: dashboardVisitLoadItems } = useAdminPagination(
    getDashboardVisitsEndpoint,
    setDashboardVisits,
    console.error,
  );

  const [dashboardPreSaleVisits, setDashboardPreSaleVisits] = useState<
    ProperPreSaleVisit[]
  >([]);
  const { paginator: paginatorPreSaleVisit, loadItems: itemsPreSaleVisits } =
    useAdminPagination(
      getDashboardPreSaleVisitsEndpoint,
      setDashboardPreSaleVisits,
      console.error,
    );

  if (!dashboardMetrics || !dashboardPreSaleMetrics) {
    return <>Cargando</>;
  }

  const rentalInfo = toStatsMetrics(dashboardMetrics.visits, VISIT_STATUS_MAP);
  const preSaleInfo = toStatsMetrics(dashboardPreSaleMetrics.visits, PRE_SALE_STATUS_MAP);

  return (
    <Container className="mt-6" fluid>
      <NavigationTabDashboard
        activeTab={activeTab}
        onTabChange={setActiveTab}
        preSaleCount={preSaleInfo.total}
        rentCount={rentalInfo.total}
        secondaryCount={0}
      />

      <MetricsDateDashboard
        activeTab={activeTab}
        period={
          activeTab === 'rent'
            ? (dashboardMetrics?.period ?? defaultPeriod)
            : (dashboardPreSaleMetrics?.period ?? defaultPeriod)
        }
      />

      <MetricsDashboard
        activeTab={activeTab}
        stats={activeTab === 'rent' ? rentalInfo : preSaleInfo}
      />

      {activeTab === 'rent' && (
        <Row>
          <Col sm={12}>
            {dashboardVisits && dashboardVisits.length > 0 && (
              <DashboardVisitListTable
                visits={dashboardVisits}
                footer={
                  <AdminPaginator
                    page={paginator.page}
                    totalPages={paginator.pageCount}
                    onPageChange={dashboardVisitLoadItems}
                  />
                }
              />
            )}
          </Col>
        </Row>
      )}

      {activeTab === 'pre-sale' && (
        <Row>
          <Col sm={12}>
            {dashboardPreSaleVisits && dashboardPreSaleVisits.length > 0 && (
              <DashboardPreSaleVisitListTable
                visits={dashboardPreSaleVisits}
                footer={
                  <AdminPaginator
                    page={paginatorPreSaleVisit.page}
                    totalPages={paginatorPreSaleVisit.pageCount}
                    onPageChange={itemsPreSaleVisits}
                  />
                }
              />
            )}
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default ProperDashboard;
